import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import {
  getLabelTemplateById,
  getPaperSizeById,
  LABEL_SHEET_TEMPLATES,
  PAPER_SIZES,
} from './labelSheetTemplates';
import { parsePlaqueContent } from './plaqueContent';
import FontSelect from '../FontSelect';
import { FONT_OPTIONS } from '../tvTemplates';
import '../tvPreview.css';

const PX_PER_MM = 96 / 25.4;

const mmToPx = (mm) => Number(mm || 0) * PX_PER_MM;

const clampNumber = (value, { min = 0, max = Infinity, fallback = 0 } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

const escapeHtml = (text) => String(text || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
  reader.readAsDataURL(blob);
});

const triggerDownload = (dataUrl, filename) => {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename || 'download.png';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const makeCatalogConfig = (catalogType) => {
  const type = String(catalogType || '').toLowerCase();
  if (type === 'products') {
    return {
      catalogType: 'products',
      displayName: 'Products',
      landingPath: '/lp/pr/',
      unitLabel: 'Location',
      unitParam: 'loc',
      unitLabelParam: 'llabel',
    };
  }
  if (type === 'services') {
    return {
      catalogType: 'services',
      displayName: 'Services',
      landingPath: '/lp/s/',
      unitLabel: 'Location',
      unitParam: 'loc',
      unitLabelParam: 'llabel',
    };
  }
  return {
    catalogType: 'menu',
    displayName: 'Menu',
    landingPath: '/lp/m/',
    unitLabel: 'Table',
    unitParam: 'table',
    unitLabelParam: 'tlabel',
  };
};

const buildAbsoluteUrl = (path) => {
  if (!path) return '';
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
};

const buildCatalogLandingUrl = (config, landingKey, { unitNumber, unitLabel, itemId, sectionId } = {}) => {
  if (!landingKey) return '';
  const landingPath = config?.landingPath || '/lp/m/';
  const unitParam = config?.unitParam || 'table';
  const unitLabelParam = config?.unitLabelParam || 'tlabel';
  if (typeof window === 'undefined') return `${landingPath}${landingKey}`;
  const url = new URL(`${window.location.origin}${landingPath}${landingKey}`);
  if (unitNumber) url.searchParams.set(unitParam, String(unitNumber));
  if (unitLabel) url.searchParams.set(unitLabelParam, String(unitLabel));
  if (itemId) url.searchParams.set('item', String(itemId));
  if (sectionId) url.searchParams.set('section', String(sectionId));
  return url.toString();
};

const getTemplatePaperId = (templateId, paperId) => {
  if (templateId === 'custom') return String(paperId || 'A4').toUpperCase();
  const template = getLabelTemplateById(templateId);
  return template.paperId || String(paperId || 'A4').toUpperCase();
};

const getQrWidthPx = ({ sizeMm, dpi }) => {
  const safeDpi = clampNumber(dpi, { min: 72, max: 1200, fallback: 300 });
  const mm = clampNumber(sizeMm, { min: 8, max: 200, fallback: 30 });
  return Math.max(128, Math.round((mm / 25.4) * safeDpi));
};

const getFontOptions = (selected) => {
  if (!selected || FONT_OPTIONS.some((option) => option.value === selected)) return FONT_OPTIONS;
  return [...FONT_OPTIONS, { value: selected, label: selected }];
};

const safeFontFamily = (value, fallback = 'Inter') => {
  const cleaned = typeof value === 'string' ? value.trim() : '';
  return cleaned || fallback;
};

const safePosition = (value, fallback = 'bottom') => {
  const cleaned = String(value || '').toLowerCase();
  if (['top', 'bottom', 'left', 'right'].includes(cleaned)) return cleaned;
  return fallback;
};

const resolveTableTitleText = (slot, design) => {
  const tableNumber = slot?.tableNumber ? String(slot.tableNumber) : '';
  const showLabel = design?.showTableLabel !== false;
  const showNumber = design?.showTableNumber !== false;
  if (!showLabel && !showNumber) return '';

  const label = String(design?.tableLabelText || 'Table').trim();
  if (showLabel && showNumber) return `${label} ${tableNumber}`.trim();
  if (showLabel) return label;
  return tableNumber;
};

const resolveSlotTitleText = (slot, design) => {
  if (!slot) return '';
  if (slot.tableNumber) return resolveTableTitleText(slot, design);
  return String(slot.title || '');
};

const resolveSlotSubtitleText = (slot, design) => {
  if (!slot) return '';
  const base = String(slot.subtitle || '');
  if (base) return base;
  if (slot.tableNumber) return String(design?.tableSubtitleText || '');
  return '';
};

const lineClampText = (value, maxChars = 240) => {
  const cleaned = typeof value === 'string' ? value.trim() : '';
  if (!cleaned) return '';
  return cleaned.length > maxChars ? `${cleaned.slice(0, maxChars)}…` : cleaned;
};

const loadImage = (src) => new Promise((resolve) => {
  if (!src) return resolve(null);
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

const mmToPxAtDpi = (mm, dpi) => Math.round((Number(mm || 0) / 25.4) * Number(dpi || 300));

const fitTextToWidth = (ctx, text, maxWidth) => {
  const raw = String(text || '');
  if (!raw) return '';
  if (ctx.measureText(raw).width <= maxWidth) return raw;
  const ellipsis = '…';
  let left = 0;
  let right = raw.length;
  let best = '';
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const candidate = `${raw.slice(0, mid)}${ellipsis}`;
    if (ctx.measureText(candidate).width <= maxWidth) {
      best = candidate;
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  return best || ellipsis;
};

async function renderPageToCanvas({ layout, slots, design, dpi }) {
  const safeDpi = clampNumber(dpi, { min: 72, max: 1200, fallback: 300 });
  const paperWmm = Number(layout?.paper?.widthMm || 210);
  const paperHmm = Number(layout?.paper?.heightMm || 297);
  const canvas = document.createElement('canvas');
  canvas.width = mmToPxAtDpi(paperWmm, safeDpi);
  canvas.height = mmToPxAtDpi(paperHmm, safeDpi);

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const cols = Math.max(1, Math.trunc(layout?.columns || 1));
  const cellWmm = Number(layout?.labelWidthMm || 40);
  const cellHmm = Number(layout?.labelHeightMm || 20);
  const gapXmm = Number(layout?.gapXmm || 0);
  const gapYmm = Number(layout?.gapYmm || 0);
  const offsetTopMm = Number(layout?.marginTopMm || 0);
  const offsetLeftMm = Number(layout?.marginLeftMm || 0);
  const radiusMm = Number(layout?.cornerRadiusMm || 0);
  const paddingMm = 2.2;
  const borderMm = design?.showBorder ? 0.35 : 0;

  const contentStyle = String(design?.contentStyle || 'qrTitle');
  const textPosition = safePosition(design?.textPosition, 'bottom');
  const showHeader = Boolean(design?.showHeader) && contentStyle === 'qrTitle';
  const showTitle = Boolean(design?.showTitle);
  const showSubtitle = Boolean(design?.showSubtitle);
  const showPrice = Boolean(design?.showPrice);
  const showDescription = Boolean(design?.showDescription) && contentStyle === 'plaque';
  const showLogo = Boolean(design?.showLogo) && contentStyle === 'plaque';

  const headerText = String(design?.headerText || '');
  const desiredQrMm = clampNumber(design?.qrSizeMm, { min: 8, max: 200, fallback: 30 });
  const desiredLogoMm = clampNumber(design?.logoSizeMm, { min: 6, max: 60, fallback: 18 });

  const headerTyp = computeEffectiveTypographyMm({
    cellWmm,
    cellHmm,
    typography: design?.headerTypography || { fontFamily: 'Inter', sizeMm: 3.4 },
    kind: 'header',
  });
  const titleTyp = computeEffectiveTypographyMm({
    cellWmm,
    cellHmm,
    typography: design?.titleTypography || { fontFamily: 'Inter', sizeMm: 3.6 },
    kind: 'title',
  });
  const subtitleTyp = computeEffectiveTypographyMm({
    cellWmm,
    cellHmm,
    typography: design?.subtitleTypography || { fontFamily: 'Inter', sizeMm: 3.1 },
    kind: 'subtitle',
  });
  const priceTyp = computeEffectiveTypographyMm({
    cellWmm,
    cellHmm,
    typography: design?.priceTypography || { fontFamily: 'Inter', sizeMm: 3.2 },
    kind: 'price',
  });
  const descTyp = computeEffectiveTypographyMm({
    cellWmm,
    cellHmm,
    typography: design?.descriptionTypography || { fontFamily: 'Inter', sizeMm: 3.0 },
    kind: 'description',
  });

  const ensureFonts = async () => {
    const families = new Set([
      headerTyp.fontFamily,
      titleTyp.fontFamily,
      subtitleTyp.fontFamily,
      priceTyp.fontFamily,
      descTyp.fontFamily,
    ]);
    if (!document.fonts?.load) return;
    await Promise.all(
      Array.from(families).map((family) => document.fonts.load(`16px "${family}"`).catch(() => null))
    );
  };
  await ensureFonts();

  const strokeRoundedRect = (x, y, w, h, r) => {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
    if (borderMm) ctx.stroke();
  };

  for (let i = 0; i < (Array.isArray(slots) ? slots.length : 0); i += 1) {
    const slot = slots[i];
    const row = Math.floor(i / cols);
    const col = i % cols;

    const xMm = offsetLeftMm + col * (cellWmm + gapXmm);
    const yMm = offsetTopMm + row * (cellHmm + gapYmm);

    const x = mmToPxAtDpi(xMm, safeDpi);
    const y = mmToPxAtDpi(yMm, safeDpi);
    const w = mmToPxAtDpi(cellWmm, safeDpi);
    const h = mmToPxAtDpi(cellHmm, safeDpi);
    const r = mmToPxAtDpi(radiusMm, safeDpi);
    const pad = mmToPxAtDpi(paddingMm, safeDpi);

    if (borderMm) {
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = Math.max(1, mmToPxAtDpi(borderMm, safeDpi));
      strokeRoundedRect(x + ctx.lineWidth / 2, y + ctx.lineWidth / 2, w - ctx.lineWidth, h - ctx.lineWidth, r);
    }

    if (!slot) continue;

    const qrImg = await loadImage(slot.qrDataUrl);
    const logoImg = showLogo ? await loadImage(slot.logoDataUrl) : null;

    const title = resolveSlotTitleText(slot, design);
    const subtitle = resolveSlotSubtitleText(slot, design);
    const price = String(slot.priceText || '');
    const desc = lineClampText(slot.description || '', 320);
    const header = String(slot.headerText || headerText || title || 'Scan');

    ctx.fillStyle = '#111111';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    const cellInnerWmm = cellWmm - paddingMm * 2;
    const cellInnerHmm = cellHmm - paddingMm * 2;

    if (contentStyle === 'qrOnly') {
      const qrMm = computeQrSizeMm({
        cellWmm,
        cellHmm,
        paddingMm,
        desiredQrMm,
        textPosition: 'bottom',
        reservedTextMm: 0,
      });
      const qrPx = mmToPxAtDpi(qrMm, safeDpi);
      const cx = x + w / 2;
      const cy = y + h / 2;
      if (qrImg) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(qrImg, cx - qrPx / 2, cy - qrPx / 2, qrPx, qrPx);
      }
      continue;
    }

    if (contentStyle === 'plaque') {
      const logoMm = showLogo ? Math.min(desiredLogoMm, cellInnerHmm * 0.28) : 0;
      const logoPx = mmToPxAtDpi(logoMm, safeDpi);

      const titleFontPx = mmToPxAtDpi(titleTyp.sizeMm, safeDpi);
      const subtitleFontPx = mmToPxAtDpi(subtitleTyp.sizeMm, safeDpi);
      const priceFontPx = mmToPxAtDpi(priceTyp.sizeMm, safeDpi);
      const descFontPx = mmToPxAtDpi(descTyp.sizeMm, safeDpi);

      const textStartX = x + pad + (logoMm ? logoPx + mmToPxAtDpi(2.4, safeDpi) : 0);
      const textMaxW = x + w - pad - textStartX;

      let cursorY = y + pad + titleFontPx;

      if (showLogo && logoMm) {
        if (logoImg) {
          ctx.imageSmoothingEnabled = true;
          ctx.drawImage(logoImg, x + pad, y + pad, logoPx, logoPx);
        } else {
          ctx.fillStyle = '#eeeeee';
          ctx.fillRect(x + pad, y + pad, logoPx, logoPx);
          ctx.fillStyle = '#111111';
        }
      }

      if (showTitle && title) {
        ctx.font = `800 ${titleFontPx}px "${safeFontFamily(titleTyp.fontFamily)}", sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(fitTextToWidth(ctx, title, textMaxW), textStartX, cursorY);
        cursorY += titleFontPx * 1.25;
      }

      if (showSubtitle && subtitle) {
        ctx.font = `600 ${subtitleFontPx}px "${safeFontFamily(subtitleTyp.fontFamily)}", sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(fitTextToWidth(ctx, subtitle, textMaxW), textStartX, cursorY);
        cursorY += subtitleFontPx * 1.15;
      }

      if (showPrice && price) {
        ctx.font = `800 ${priceFontPx}px "${safeFontFamily(priceTyp.fontFamily)}", sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(fitTextToWidth(ctx, price, textMaxW), textStartX, cursorY);
        cursorY += priceFontPx * 1.1;
      }

      if (showDescription && desc) {
        ctx.font = `400 ${descFontPx}px "${safeFontFamily(descTyp.fontFamily)}", sans-serif`;
        ctx.textAlign = 'left';
        const lines = wrapLines(ctx, desc, textMaxW, 4);
        for (const line of lines) {
          ctx.fillText(fitTextToWidth(ctx, line, textMaxW), textStartX, cursorY);
          cursorY += descFontPx * 1.18;
        }
      }

      const reservedTextMm = (cursorY - (y + pad)) / mmToPxAtDpi(1, safeDpi) + 2.5;
      const qrMm = computeQrSizeMm({
        cellWmm,
        cellHmm,
        paddingMm,
        desiredQrMm,
        textPosition: 'bottom',
        reservedTextMm: Math.min(cellInnerHmm * 0.55, reservedTextMm),
      });
      const qrPx = mmToPxAtDpi(qrMm, safeDpi);
      const qrX = x + w / 2 - qrPx / 2;
      const qrY = y + h - pad - qrPx;
      if (qrImg) {
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(qrImg, qrX, qrY, qrPx, qrPx);
      }
      continue;
    }

    // qrTitle
    const headerFontPx = mmToPxAtDpi(headerTyp.sizeMm, safeDpi);
    const titleFontPx = mmToPxAtDpi(titleTyp.sizeMm, safeDpi);
    const subtitleFontPx = mmToPxAtDpi(subtitleTyp.sizeMm, safeDpi);
    const priceFontPx = mmToPxAtDpi(priceTyp.sizeMm, safeDpi);
    const gapPx = mmToPxAtDpi(1.6, safeDpi);

    const lines = [];
    if (showTitle && title) lines.push({ kind: 'title', text: title });
    if (showSubtitle && subtitle) lines.push({ kind: 'subtitle', text: subtitle });
    if (showPrice && price) lines.push({ kind: 'price', text: price });

    const reservedTextMm = (textPosition === 'top' || textPosition === 'bottom')
      ? (paddingMm * 2)
        + (showHeader ? headerTyp.sizeMm * 1.6 + 1.2 : 0)
        + (showTitle && title ? titleTyp.sizeMm * 1.35 + 0.8 : 0)
        + (showSubtitle && subtitle ? subtitleTyp.sizeMm * 1.25 + 0.6 : 0)
        + (showPrice && price ? priceTyp.sizeMm * 1.25 + 0.6 : 0)
      : 0;

    let qrMm = computeQrSizeMm({
      cellWmm,
      cellHmm,
      paddingMm,
      desiredQrMm,
      textPosition,
      reservedTextMm,
    });

    const minTextWidthMm = 16;
    if (textPosition === 'left' || textPosition === 'right') {
      const maxByWidth = cellInnerWmm - minTextWidthMm;
      qrMm = Math.min(qrMm, maxByWidth);
    }

    const qrPx = mmToPxAtDpi(qrMm, safeDpi);

    const innerX = x + pad;
    const innerY = y + pad;
    const innerW = w - pad * 2;
    const innerH = h - pad * 2;

    const drawTextBlock = (tx, ty, tw, th) => {
      let cursorY = ty;
      ctx.textAlign = 'center';
      const centerX = tx + tw / 2;
      const maxW = tw;

      if (showHeader) {
        ctx.font = `800 ${headerFontPx}px "${safeFontFamily(headerTyp.fontFamily)}", sans-serif`;
        const rendered = fitTextToWidth(ctx, header.toUpperCase(), maxW);
        cursorY += headerFontPx;
        ctx.fillText(rendered, centerX, cursorY);
        cursorY += gapPx;
      }

      for (const line of lines) {
        if (!line.text) continue;
        if (line.kind === 'title') ctx.font = `800 ${titleFontPx}px "${safeFontFamily(titleTyp.fontFamily)}", sans-serif`;
        if (line.kind === 'subtitle') ctx.font = `600 ${subtitleFontPx}px "${safeFontFamily(subtitleTyp.fontFamily)}", sans-serif`;
        if (line.kind === 'price') ctx.font = `800 ${priceFontPx}px "${safeFontFamily(priceTyp.fontFamily)}", sans-serif`;
        const fontPx = line.kind === 'title' ? titleFontPx : line.kind === 'subtitle' ? subtitleFontPx : priceFontPx;
        const rendered = fitTextToWidth(ctx, line.text, maxW);
        cursorY += fontPx;
        if (cursorY > ty + th) break;
        ctx.fillText(rendered, centerX, cursorY);
        cursorY += gapPx * 0.4;
      }
    };

    const drawQr = (qx, qy) => {
      if (!qrImg) return;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(qrImg, qx, qy, qrPx, qrPx);
    };

    if (textPosition === 'top') {
      const textH = Math.max(0, innerH - qrPx - gapPx);
      drawTextBlock(innerX, innerY, innerW, textH);
      drawQr(innerX + innerW / 2 - qrPx / 2, innerY + textH + gapPx);
      continue;
    }
    if (textPosition === 'bottom') {
      const textH = Math.max(0, innerH - qrPx - gapPx);
      drawQr(innerX + innerW / 2 - qrPx / 2, innerY);
      drawTextBlock(innerX, innerY + qrPx + gapPx, innerW, textH);
      continue;
    }
    if (textPosition === 'left') {
      const textW = Math.max(mmToPxAtDpi(minTextWidthMm, safeDpi), innerW - qrPx - gapPx);
      drawTextBlock(innerX, innerY, textW, innerH);
      drawQr(innerX + textW + gapPx, innerY + innerH / 2 - qrPx / 2);
      continue;
    }
    // right
    const textW = Math.max(mmToPxAtDpi(minTextWidthMm, safeDpi), innerW - qrPx - gapPx);
    drawQr(innerX, innerY + innerH / 2 - qrPx / 2);
    drawTextBlock(innerX + qrPx + gapPx, innerY, textW, innerH);
  }

  return canvas;
}

const printHtmlInIframe = async (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.setAttribute('aria-hidden', 'true');
  document.body.appendChild(iframe);

  const cleanup = () => {
    try {
      iframe.remove();
    } catch (err) {
      // ignore
    }
  };

  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) throw new Error('Print frame not available');

    doc.open();
    doc.write(html);
    doc.close();

    const waitForLoad = () => new Promise((resolve) => {
      if (doc.readyState === 'complete') return resolve();
      iframe.onload = () => resolve();
      setTimeout(() => resolve(), 250);
    });
    await waitForLoad();

    const images = Array.from(doc.images || []);
    await Promise.all(
      images.map((img) => new Promise((resolve) => {
        if (img.complete) return resolve();
        img.onload = () => resolve();
        img.onerror = () => resolve();
      }))
    );

    const win = iframe.contentWindow;
    if (!win) throw new Error('Print window not available');
    win.focus();
    win.print();

    setTimeout(() => cleanup(), 800);
  } catch (err) {
    cleanup();
    throw err;
  }
};

function computeEffectiveTypographyMm({ cellWmm, cellHmm, typography, kind }) {
  const maxByKind = {
    header: Math.max(2.2, cellHmm * 0.16),
    title: Math.max(2.0, cellHmm * 0.12),
    subtitle: Math.max(1.8, cellHmm * 0.105),
    price: Math.max(1.9, cellHmm * 0.115),
    description: Math.max(1.6, cellHmm * 0.09),
  };
  const maxMm = maxByKind[kind] || Math.max(1.8, cellHmm * 0.11);
  const requested = clampNumber(typography?.sizeMm, { min: 1.2, max: 30, fallback: 3.2 });
  const sizeMm = Math.min(requested, maxMm);
  return {
    ...typography,
    sizeMm,
    fontFamily: safeFontFamily(typography?.fontFamily),
  };
}

function computeQrSizeMm({ cellWmm, cellHmm, paddingMm, desiredQrMm, textPosition, reservedTextMm, minTextWidthMm = 16 }) {
  const baseMax = Math.min(cellWmm - paddingMm * 2, cellHmm - paddingMm * 2);
  let maxMm = baseMax;

  if (textPosition === 'top' || textPosition === 'bottom') {
    maxMm = Math.min(cellWmm - paddingMm * 2, cellHmm - paddingMm * 2 - reservedTextMm);
  } else if (textPosition === 'left' || textPosition === 'right') {
    const maxByHeight = cellHmm - paddingMm * 2;
    const maxByWidth = cellWmm - paddingMm * 2 - minTextWidthMm;
    maxMm = Math.min(maxByHeight, maxByWidth);
  }

  const qrMm = clampNumber(desiredQrMm, { min: 8, max: 200, fallback: 30 });
  return Math.max(8, Math.min(qrMm, maxMm));
}

function wrapLines(ctx, text, maxWidth, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const next = `${current} ${words[i]}`;
    if (ctx.measureText(next).width <= maxWidth) {
      current = next;
      continue;
    }
    lines.push(current);
    current = words[i];
    if (lines.length >= maxLines - 1) break;
  }
  lines.push(current);
  if (lines.length > maxLines) return lines.slice(0, maxLines);
  return lines;
}

function buildLabelLayout({ paper, template, startRow, startCol, offsetTopMm, offsetLeftMm }) {
  const columns = Math.max(1, Math.trunc(template?.columns || 1));
  const rows = Math.max(1, Math.trunc(template?.rows || 1));
  const baseMarginTopMm = clampNumber(template?.marginTopMm, { min: 0, max: 50, fallback: 0 });
  const baseMarginLeftMm = clampNumber(template?.marginLeftMm, { min: 0, max: 50, fallback: 0 });
  const marginTopMm = baseMarginTopMm + clampNumber(offsetTopMm, { min: -20, max: 20, fallback: 0 });
  const marginLeftMm = baseMarginLeftMm + clampNumber(offsetLeftMm, { min: -20, max: 20, fallback: 0 });
  const pitchXmm = clampNumber(template?.pitchXmm, { min: 1, max: paper.widthMm, fallback: template?.labelWidthMm || 1 });
  const pitchYmm = clampNumber(template?.pitchYmm, { min: 1, max: paper.heightMm, fallback: template?.labelHeightMm || 1 });
  const labelWidthMm = clampNumber(template?.labelWidthMm, { min: 1, max: paper.widthMm, fallback: 40 });
  const labelHeightMm = clampNumber(template?.labelHeightMm, { min: 1, max: paper.heightMm, fallback: 20 });

  const gapXmm = Math.max(0, pitchXmm - labelWidthMm);
  const gapYmm = Math.max(0, pitchYmm - labelHeightMm);

  const normalizedStartRow = clampNumber(startRow, { min: 1, max: rows, fallback: 1 });
  const normalizedStartCol = clampNumber(startCol, { min: 1, max: columns, fallback: 1 });
  const skip = (normalizedStartRow - 1) * columns + (normalizedStartCol - 1);
  const slotCount = rows * columns;

  return {
    kind: 'label-sheet',
    paper,
    columns,
    rows,
    slotCount,
    skip,
    marginTopMm,
    marginLeftMm,
    labelWidthMm,
    labelHeightMm,
    gapXmm,
    gapYmm,
    cornerRadiusMm: clampNumber(template?.cornerRadiusMm, { min: 0, max: 15, fallback: 0 }),
  };
}

function buildGridLayout({ paper, grid }) {
  const columns = Math.max(1, Math.trunc(grid?.columns || 1));
  const rows = Math.max(1, Math.trunc(grid?.rows || 1));
  const marginMm = clampNumber(grid?.marginMm, { min: 0, max: 40, fallback: 10 });
  const gapMm = clampNumber(grid?.gapMm, { min: 0, max: 30, fallback: 6 });
  const slotCount = rows * columns;

  const usableWidth = Math.max(1, paper.widthMm - marginMm * 2);
  const usableHeight = Math.max(1, paper.heightMm - marginMm * 2);
  const cellWidthMm = (usableWidth - gapMm * (columns - 1)) / columns;
  const cellHeightMm = (usableHeight - gapMm * (rows - 1)) / rows;

  return {
    kind: 'sheet-grid',
    paper,
    columns,
    rows,
    slotCount,
    skip: 0,
    marginTopMm: marginMm,
    marginLeftMm: marginMm,
    labelWidthMm: cellWidthMm,
    labelHeightMm: cellHeightMm,
    gapXmm: gapMm,
    gapYmm: gapMm,
    cornerRadiusMm: clampNumber(grid?.cornerRadiusMm, { min: 0, max: 30, fallback: 6 }),
  };
}

function buildPrintHtml({ title, layout, pages, design }) {
  const safeTitle = escapeHtml(title || 'Print');
  const pageWidth = Number(layout?.paper?.widthMm || 210);
  const pageHeight = Number(layout?.paper?.heightMm || 297);
  const paddingTop = Number(layout?.marginTopMm || 0);
  const paddingLeft = Number(layout?.marginLeftMm || 0);
  const cols = Math.max(1, Math.trunc(layout?.columns || 1));
  const rows = Math.max(1, Math.trunc(layout?.rows || 1));
  const gapX = Number(layout?.gapXmm || 0);
  const gapY = Number(layout?.gapYmm || 0);
  const cellWidth = Number(layout?.labelWidthMm || 40);
  const cellHeight = Number(layout?.labelHeightMm || 20);
  const cornerRadius = Number(layout?.cornerRadiusMm || 0);

  const contentStyle = String(design?.contentStyle || 'qrTitle');
  const showLogo = Boolean(design?.showLogo);
  const showTitle = Boolean(design?.showTitle);
  const showSubtitle = Boolean(design?.showSubtitle);
  const showDescription = Boolean(design?.showDescription);
  const showPrice = Boolean(design?.showPrice);
  const showHeader = Boolean(design?.showHeader);
  const showBorder = Boolean(design?.showBorder);
  const headerText = String(design?.headerText || '');
  const textPosition = safePosition(design?.textPosition, 'bottom');

  const headerFontFamily = safeFontFamily(design?.headerTypography?.fontFamily || design?.headerFontFamily);
  const titleFontFamily = safeFontFamily(design?.titleTypography?.fontFamily || design?.titleFontFamily);
  const subtitleFontFamily = safeFontFamily(design?.subtitleTypography?.fontFamily || design?.subtitleFontFamily);
  const priceFontFamily = safeFontFamily(design?.priceTypography?.fontFamily || design?.priceFontFamily);
  const descriptionFontFamily = safeFontFamily(design?.descriptionTypography?.fontFamily || design?.descriptionFontFamily);

  const headerFontMm = clampNumber(design?.headerTypography?.sizeMm ?? design?.headerFontSizeMm, { min: 1.2, max: 30, fallback: 3.4 });
  const titleFontMm = clampNumber(design?.titleTypography?.sizeMm ?? design?.titleFontSizeMm, { min: 1.2, max: 30, fallback: 3.6 });
  const subtitleFontMm = clampNumber(design?.subtitleTypography?.sizeMm ?? design?.subtitleFontSizeMm, { min: 1.2, max: 30, fallback: 3.1 });
  const priceFontMm = clampNumber(design?.priceTypography?.sizeMm ?? design?.priceFontSizeMm, { min: 1.2, max: 30, fallback: 3.2 });
  const descriptionFontMm = clampNumber(design?.descriptionTypography?.sizeMm ?? design?.descriptionFontSizeMm, { min: 1.2, max: 30, fallback: 3.0 });

  const logoSizeMm = clampNumber(design?.logoSizeMm, { min: 6, max: 60, fallback: 18 });
  const qrSizeMm = clampNumber(design?.qrSizeMm, { min: 10, max: 200, fallback: 30 });
  const paddingMm = 2.2;

  const effectiveHeaderMm = computeEffectiveTypographyMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    typography: { fontFamily: headerFontFamily, sizeMm: headerFontMm },
    kind: 'header',
  }).sizeMm;
  const effectiveTitleMm = computeEffectiveTypographyMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    typography: { fontFamily: titleFontFamily, sizeMm: titleFontMm },
    kind: 'title',
  }).sizeMm;
  const effectiveSubtitleMm = computeEffectiveTypographyMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    typography: { fontFamily: subtitleFontFamily, sizeMm: subtitleFontMm },
    kind: 'subtitle',
  }).sizeMm;
  const effectivePriceMm = computeEffectiveTypographyMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    typography: { fontFamily: priceFontFamily, sizeMm: priceFontMm },
    kind: 'price',
  }).sizeMm;
  const effectiveDescMm = computeEffectiveTypographyMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    typography: { fontFamily: descriptionFontFamily, sizeMm: descriptionFontMm },
    kind: 'description',
  }).sizeMm;

  const estimateTextHeightMm = () => {
    if (contentStyle !== 'qrTitle') return 0;
    if (textPosition === 'left' || textPosition === 'right') return 0;
    let mm = paddingMm;
    if (showHeader) mm += effectiveHeaderMm * 1.6 + 1.2;
    if (showTitle) mm += effectiveTitleMm * 1.35 + 0.8;
    if (showSubtitle) mm += effectiveSubtitleMm * 1.25 + 0.6;
    if (showPrice) mm += effectivePriceMm * 1.25 + 0.6;
    mm += paddingMm;
    return mm;
  };

  const reservedTextMm = estimateTextHeightMm();
  const effectiveQrMm = computeQrSizeMm({
    cellWmm: cellWidth,
    cellHmm: cellHeight,
    paddingMm,
    desiredQrMm: qrSizeMm,
    textPosition,
    reservedTextMm,
  });

  const renderCells = (slots, pageIndex) => (Array.isArray(slots) ? slots : []).map((slot, idx) => {
    const absoluteIndex = pageIndex * rows * cols + idx;
    if (!slot) {
      return `<div class="cell empty" data-idx="${absoluteIndex}"></div>`;
    }

    const resolvedTitle = resolveSlotTitleText(slot, design);
    const resolvedSubtitle = resolveSlotSubtitleText(slot, design);
    const safeItemTitle = escapeHtml(resolvedTitle || '');
    const safeSubtitle = escapeHtml(resolvedSubtitle || '');
    const safeDescription = escapeHtml(slot.description || '');
    const safePrice = escapeHtml(slot.priceText || '');
    const safeHeader = escapeHtml(slot.headerText || headerText || '');
    const safeQr = String(slot.qrDataUrl || '');
    const safeLogo = String(slot.logoDataUrl || '');

    const header = showHeader && (safeHeader || safeItemTitle)
      ? `<div class="header">${safeHeader || safeItemTitle}</div>`
      : '';

    const titleBlock = (showTitle && safeItemTitle) ? `<div class="title">${safeItemTitle}</div>` : '';
    const subtitleBlock = (showSubtitle && safeSubtitle) ? `<div class="subtitle">${safeSubtitle}</div>` : '';
    const descriptionBlock = (showDescription && safeDescription) ? `<div class="desc">${safeDescription}</div>` : '';
    const priceBlock = (showPrice && safePrice) ? `<div class="price">${safePrice}</div>` : '';
    const logoBlock = (showLogo && safeLogo) ? `<img class="logo" src="${safeLogo}" alt="" />` : '';

    if (contentStyle === 'qrOnly') {
      return `
        <div class="cell" data-idx="${absoluteIndex}">
          <div class="qrWrap">
            <img class="qr" src="${safeQr}" alt="QR" />
          </div>
        </div>
      `;
    }

    if (contentStyle === 'plaque') {
      return `
        <div class="cell plaque" data-idx="${absoluteIndex}">
          <div class="plaqueTop">
            ${logoBlock}
            <div class="plaqueText">
              ${titleBlock}
              ${subtitleBlock}
              ${priceBlock}
              ${descriptionBlock}
            </div>
          </div>
          <div class="plaqueBottom">
            <img class="qr" src="${safeQr}" alt="QR" />
          </div>
        </div>
      `;
    }

    const meta = `
      <div class="meta">
        ${showTitle && safeItemTitle && (!showHeader || !safeHeader || safeHeader !== safeItemTitle) ? titleBlock : ''}
        ${subtitleBlock}
        ${priceBlock}
      </div>
    `;

    const textBlock = `
      <div class="textBlock">
        ${header}
        ${meta}
      </div>
    `;

    const qrBlock = `
      <div class="qrBlock">
        <img class="qr" src="${safeQr}" alt="QR" />
      </div>
    `;

    return `
      <div class="cell framed ${textPosition}" data-idx="${absoluteIndex}">
        ${textPosition === 'top' ? `${textBlock}${qrBlock}` : ''}
        ${textPosition === 'bottom' ? `${qrBlock}${textBlock}` : ''}
        ${textPosition === 'left' ? `${textBlock}${qrBlock}` : ''}
        ${textPosition === 'right' ? `${qrBlock}${textBlock}` : ''}
      </div>
    `;
  }).join('\n');

  const renderedPages = (Array.isArray(pages) ? pages : []).map((slots, idx) => `
    <div class="page">
      <div class="grid">${renderCells(slots, idx)}</div>
    </div>
  `).join('\n');

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${safeTitle}</title>
        <style>
          @page { size: ${pageWidth}mm ${pageHeight}mm; margin: 0; }
          html, body { margin: 0; padding: 0; background: white; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; }
          .page { width: ${pageWidth}mm; height: ${pageHeight}mm; padding-top: ${paddingTop}mm; padding-left: ${paddingLeft}mm; box-sizing: border-box; page-break-after: always; }
          .page:last-child { page-break-after: auto; }
          .grid { display: grid; grid-template-columns: repeat(${cols}, ${cellWidth}mm); grid-auto-rows: ${cellHeight}mm; column-gap: ${gapX}mm; row-gap: ${gapY}mm; }
          .cell { box-sizing: border-box; border-radius: ${cornerRadius}mm; overflow: hidden; background: white; }
          .cell.empty { background: transparent; }
          .cell.framed { border: ${showBorder ? 0.35 : 0}mm solid #111; display: flex; }
          .cell.framed.top, .cell.framed.bottom { flex-direction: column; }
          .cell.framed.left, .cell.framed.right { flex-direction: row; }
          .textBlock { display: flex; flex-direction: column; min-width: 0; }
          .cell.framed.left .textBlock, .cell.framed.right .textBlock { flex: 1; }
          .qrBlock { flex: 1; display: flex; align-items: center; justify-content: center; padding: ${paddingMm}mm; box-sizing: border-box; }
          .cell.framed.left .qrBlock, .cell.framed.right .qrBlock { flex: 0 0 auto; }
          .header { padding: ${paddingMm}mm ${paddingMm}mm 1.4mm; font-size: min(${effectiveHeaderMm}mm, ${cellHeight * 0.16}mm); font-weight: 800; text-align: center; border-bottom: 0.25mm solid #111; letter-spacing: 0.04em; text-transform: uppercase; font-family: '${escapeHtml(headerFontFamily)}', sans-serif; overflow: hidden; }
          .meta { padding: 1.3mm ${paddingMm}mm ${paddingMm}mm; text-align: center; overflow: hidden; }
          .title { font-size: min(${effectiveTitleMm}mm, ${cellHeight * 0.12}mm); font-weight: 800; line-height: 1.1; font-family: '${escapeHtml(titleFontFamily)}', sans-serif; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .subtitle { font-size: min(${effectiveSubtitleMm}mm, ${cellHeight * 0.105}mm); font-weight: 600; opacity: 0.9; margin-top: 0.6mm; font-family: '${escapeHtml(subtitleFontFamily)}', sans-serif; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .price { font-size: min(${effectivePriceMm}mm, ${cellHeight * 0.115}mm); font-weight: 800; margin-top: 0.6mm; font-family: '${escapeHtml(priceFontFamily)}', sans-serif; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .qr { width: ${effectiveQrMm}mm; height: ${effectiveQrMm}mm; object-fit: contain; background: white; }
          .qrWrap { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding: ${paddingMm}mm; box-sizing: border-box; }
          .plaque { border: ${showBorder ? 0.35 : 0}mm solid #111; padding: 3mm; display: flex; flex-direction: column; gap: 2mm; }
          .plaqueTop { display: flex; gap: 3mm; align-items: flex-start; }
          .logo { width: ${logoSizeMm}mm; height: ${logoSizeMm}mm; object-fit: contain; border-radius: 2mm; }
          .plaqueText { flex: 1; min-width: 0; }
          .desc { font-size: min(${effectiveDescMm}mm, ${cellHeight * 0.09}mm); line-height: 1.2; margin-top: 1.4mm; font-family: '${escapeHtml(descriptionFontFamily)}', sans-serif; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 6; -webkit-box-orient: vertical; }
          .plaqueBottom { display: flex; align-items: center; justify-content: center; flex: 1; }
          @media print {
            .cell { break-inside: avoid; page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        ${renderedPages}
      </body>
    </html>
  `;
}

export default function PrintShop({ site, area }) {
  const [step, setStep] = useState('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plaques, setPlaques] = useState([]);
  const [catalogs, setCatalogs] = useState({ menu: null, products: null, services: null });
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [sourceFilter, setSourceFilter] = useState({
    plaques: true,
    menu: true,
    products: true,
    services: true,
    tables: true,
  });
  const [search, setSearch] = useState('');

  const [layoutKind, setLayoutKind] = useState('label-sheet');
  const [labelTemplateId, setLabelTemplateId] = useState(() => {
    const firstA4 = LABEL_SHEET_TEMPLATES.find((t) => t.paperId === 'A4') || LABEL_SHEET_TEMPLATES[0];
    return firstA4?.id || 'A4_3x7_63.5x38.1';
  });
  const [paperId, setPaperId] = useState('A4');
  const [startRow, setStartRow] = useState(1);
  const [startCol, setStartCol] = useState(1);
  const [offsetTopMm, setOffsetTopMm] = useState(0);
  const [offsetLeftMm, setOffsetLeftMm] = useState(0);
  const [grid, setGrid] = useState({ columns: 2, rows: 3, marginMm: 10, gapMm: 6, cornerRadiusMm: 6 });

	  const [design, setDesign] = useState({
	    contentStyle: 'qrTitle',
	    textPosition: 'bottom',
	    showBorder: true,
	    showHeader: true,
	    headerText: 'Scan to View',
	    tableLabelText: 'Table',
	    showTableLabel: true,
	    showTableNumber: true,
	    tableSubtitleText: '',
	    showTitle: true,
	    showSubtitle: false,
	    showPrice: false,
	    showDescription: true,
	    showLogo: false,
    headerTypography: { fontFamily: 'Inter', sizeMm: 3.4 },
    titleTypography: { fontFamily: 'Inter', sizeMm: 3.6 },
    subtitleTypography: { fontFamily: 'Inter', sizeMm: 3.1 },
    priceTypography: { fontFamily: 'Inter', sizeMm: 3.2 },
    descriptionTypography: { fontFamily: 'Inter', sizeMm: 3.0 },
    logoSizeMm: 18,
    qrSizeMm: 30,
    renderDpi: 300,
  });

	  const [qrCache, setQrCache] = useState({});
	  const [logoCache, setLogoCache] = useState({});
	  const [plaqueDetailsCache, setPlaqueDetailsCache] = useState({});
	  const printingRef = useRef(false);
	  const previewViewportRef = useRef(null);
	  const [previewViewport, setPreviewViewport] = useState({ width: 0, height: 0 });

  const siteId = site?.id || null;
  const areaId = area?.id || null;

  const fetchTemplates = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !siteId) {
      setTemplates([]);
      setActiveTemplateId('');
      return;
    }
    setTemplatesLoading(true);
    try {
      const qs = new URLSearchParams({ siteId: String(siteId) });
      if (areaId) qs.set('areaId', String(areaId));
      const res = await fetch(`/api/print-templates?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load templates (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const next = Array.isArray(data.templates) ? data.templates : [];
      setTemplates(next);
      const lastIdKey = `printShop:lastTemplate:${siteId}:${areaId || 'na'}`;
      const lastId = localStorage.getItem(lastIdKey) || '';
      if (lastId && next.some((t) => String(t.id) === String(lastId))) {
        setActiveTemplateId(String(lastId));
      }
    } catch (err) {
      console.warn('Failed to load templates', err);
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, [areaId, siteId]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const nextCatalogs = {};

      if (siteId) {
        const plaquesRes = await fetch(`/api/plaques?siteId=${encodeURIComponent(siteId)}`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!plaquesRes.ok) {
          const body = await plaquesRes.text().catch(() => '');
          throw new Error(body || `Failed to load plaques (${plaquesRes.status})`);
        }
        const plaquesData = await plaquesRes.json().catch(() => ({}));
        setPlaques(Array.isArray(plaquesData.plaques) ? plaquesData.plaques : []);
      } else {
        setPlaques([]);
      }

      if (areaId) {
        for (const type of ['menu', 'products', 'services']) {
          const res = await fetch(`/api/catalogs/${type}/area/${encodeURIComponent(areaId)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(body || `Failed to load ${type} (${res.status})`);
          }
          const data = await res.json().catch(() => ({}));
          nextCatalogs[type] = {
            catalog: data.catalog || null,
            menu: data.menu || null,
            sections: Array.isArray(data.sections) ? data.sections : [],
            items: Array.isArray(data.items) ? data.items : [],
          };
        }
      } else {
        nextCatalogs.menu = null;
        nextCatalogs.products = null;
        nextCatalogs.services = null;
      }

      setCatalogs((prev) => ({
        ...prev,
        ...nextCatalogs,
      }));
    } catch (err) {
      console.error('Failed to load print shop data', err);
      setError(err.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [areaId, siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

	  useEffect(() => {
	    if (step === 'layout' && !selectedKeys.size) setStep('choose');
	  }, [selectedKeys.size, step]);

	  useEffect(() => {
	    if (step !== 'layout') return undefined;
	    const el = previewViewportRef.current;
	    if (!el) return undefined;

	    const update = () => {
	      setPreviewViewport({
	        width: el.clientWidth || 0,
	        height: el.clientHeight || 0,
	      });
	    };

	    update();

	    if (!window.ResizeObserver) return undefined;
	    const ro = new ResizeObserver(() => update());
	    ro.observe(el);
	    return () => ro.disconnect();
	  }, [step]);

  const buildTemplateConfig = useCallback(() => ({
    version: 1,
    layoutKind,
    paperId,
    labelTemplateId,
    startRow: Number(startRow) || 1,
    startCol: Number(startCol) || 1,
    offsetTopMm: Number(offsetTopMm) || 0,
    offsetLeftMm: Number(offsetLeftMm) || 0,
    grid,
    design,
    sourceFilter,
  }), [
    design,
    grid,
    labelTemplateId,
    layoutKind,
    offsetLeftMm,
    offsetTopMm,
    paperId,
    sourceFilter,
    startCol,
    startRow,
  ]);

  const applyTemplateConfig = useCallback((config) => {
    if (!config || typeof config !== 'object') return;
    if (config.layoutKind) setLayoutKind(config.layoutKind);
    if (config.paperId) setPaperId(String(config.paperId).toUpperCase());
    if (config.labelTemplateId) setLabelTemplateId(String(config.labelTemplateId));
    if (config.startRow) setStartRow(config.startRow);
    if (config.startCol) setStartCol(config.startCol);
    if (config.offsetTopMm !== undefined) setOffsetTopMm(config.offsetTopMm);
    if (config.offsetLeftMm !== undefined) setOffsetLeftMm(config.offsetLeftMm);
    if (config.grid && typeof config.grid === 'object') setGrid((prev) => ({ ...prev, ...config.grid }));
    if (config.design && typeof config.design === 'object') setDesign((prev) => ({ ...prev, ...config.design }));
    if (config.sourceFilter && typeof config.sourceFilter === 'object') {
      setSourceFilter((prev) => ({ ...prev, ...config.sourceFilter }));
    }
  }, []);

  const activeTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(activeTemplateId)) || null,
    [activeTemplateId, templates]
  );

  useEffect(() => {
    if (!activeTemplate) return;
    applyTemplateConfig(activeTemplate.config || {});
    if (siteId) {
      const lastIdKey = `printShop:lastTemplate:${siteId}:${areaId || 'na'}`;
      localStorage.setItem(lastIdKey, String(activeTemplate.id));
    }
  }, [activeTemplate, applyTemplateConfig, areaId, siteId]);

  const saveNewTemplate = async () => {
    if (!siteId) {
      alert('Select a site first.');
      return;
    }
    const name = window.prompt('Template name (e.g. "A4 price tags 3x7"):', '');
    if (!name) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch('/api/print-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          siteId,
          areaId: areaId || null,
          name,
          config: buildTemplateConfig(),
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to save template (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const created = data.template || null;
      await fetchTemplates();
      if (created?.id) setActiveTemplateId(String(created.id));
    } catch (err) {
      console.error('Failed to save template', err);
      alert(err.message || 'Failed to save template.');
    }
  };

  const updateTemplate = async () => {
    if (!activeTemplate?.id) {
      alert('Select a template to update.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`/api/print-templates/${encodeURIComponent(activeTemplate.id)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          config: buildTemplateConfig(),
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to update template (${res.status})`);
      }
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to update template', err);
      alert(err.message || 'Failed to update template.');
    }
  };

  const deleteTemplate = async () => {
    if (!activeTemplate?.id) return;
    const ok = window.confirm(`Delete template "${activeTemplate.name}"?`);
    if (!ok) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`/api/print-templates/${encodeURIComponent(activeTemplate.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to delete template (${res.status})`);
      }
      setActiveTemplateId('');
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template', err);
      alert(err.message || 'Failed to delete template.');
    }
  };

  const allItems = useMemo(() => {
    const items = [];
    const normalizedSearch = String(search || '').trim().toLowerCase();

    const matchesSearch = (row) => {
      if (!normalizedSearch) return true;
      return [
        row?.title,
        row?.subtitle,
        row?.description,
        row?.sectionName,
        row?.sourceLabel,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch));
    };

    if (sourceFilter.plaques) {
      for (const plaque of Array.isArray(plaques) ? plaques : []) {
        const landingKey = plaque?.landing_key || plaque?.landingKey;
        const url = landingKey ? buildAbsoluteUrl(`/lp/p/${landingKey}`) : '';
        const row = {
          key: `plaque:${plaque.id}`,
          source: 'plaques',
          sourceLabel: 'Info Plaque',
          title: plaque.title || 'Untitled Plaque',
          subtitle: '',
          description: '',
          priceText: '',
          logoAssetId: null,
          url,
          plaqueId: plaque.id,
        };
        if (!matchesSearch(row)) continue;
        items.push(row);
      }
    }

    for (const type of ['menu', 'products', 'services']) {
      const catalogData = catalogs?.[type];
      const config = makeCatalogConfig(type);
      const catalog = catalogData?.catalog;
      if (!catalog) continue;
      if (!sourceFilter[type]) continue;

      const rootUrl = buildCatalogLandingUrl(config, catalog.landingKey);
      const rootRow = {
        key: `catalog:${type}:${catalog.id}`,
        source: type,
        sourceLabel: `${config.displayName} (Root)`,
        title: catalog.title || config.displayName,
        subtitle: '',
        description: '',
        priceText: '',
        logoAssetId: catalog.logoCreativeId || null,
        url: rootUrl,
        catalogType: type,
        catalogId: catalog.id,
      };
      if (matchesSearch(rootRow)) items.push(rootRow);

      const sections = Array.isArray(catalogData?.sections) ? catalogData.sections : [];
      const sectionNameById = new Map(sections.map((s) => [String(s.id), s.name || '']));
      const listItems = Array.isArray(catalogData?.items) ? catalogData.items : [];

      for (const item of listItems) {
        const sectionName = sectionNameById.get(String(item.sectionId)) || '';
        const url = buildCatalogLandingUrl(config, catalog.landingKey, {
          itemId: item.id,
          sectionId: item.sectionId,
        });
        const row = {
          key: `item:${type}:${item.id}`,
          source: type,
          sourceLabel: config.displayName,
          sectionName,
          title: item.name || 'Untitled',
          subtitle: item.subtitle || '',
          description: item.description || '',
          priceText: item.priceText || '',
          logoAssetId: catalog.logoCreativeId || null,
          url,
          catalogType: type,
          catalogId: catalog.id,
          itemId: item.id,
        };
        if (!matchesSearch(row)) continue;
        items.push(row);
      }
    }

    if (sourceFilter.tables) {
      const menuData = catalogs?.menu;
      const catalog = menuData?.catalog;
      const menu = menuData?.menu;
      if (catalog && menu?.tableCount) {
        const config = makeCatalogConfig('menu');
        for (let tableNumber = 1; tableNumber <= Number(menu.tableCount || 0); tableNumber += 1) {
          const label = menu?.tableLabels?.[String(tableNumber)] || `${config.unitLabel} ${tableNumber}`;
          const url = buildCatalogLandingUrl(config, catalog.landingKey, {
            unitNumber: tableNumber,
            unitLabel: label,
          });
	          const row = {
	            key: `table:${catalog.id}:${tableNumber}`,
	            source: 'tables',
	            sourceLabel: 'Menu Table QR',
	            title: label,
	            subtitle: '',
	            description: '',
	            priceText: '',
	            logoAssetId: catalog.logoCreativeId || null,
	            url,
	            catalogType: 'menu',
	            catalogId: catalog.id,
	            tableNumber,
	          };
	          if (!matchesSearch(row)) continue;
	          items.push(row);
	        }
	      }
    }

    return items;
  }, [catalogs, plaques, search, sourceFilter]);

  const filteredItems = allItems;

  const selectedItems = useMemo(() => {
    if (!selectedKeys.size) return [];
    const byKey = new Map(filteredItems.map((row) => [row.key, row]));
    const result = [];
    for (const key of selectedKeys) {
      const item = byKey.get(key);
      if (item) result.push(item);
    }
    return result;
  }, [filteredItems, selectedKeys]);

  const paper = useMemo(() => getPaperSizeById(getTemplatePaperId(labelTemplateId, paperId)), [labelTemplateId, paperId]);
  const labelTemplate = useMemo(() => (labelTemplateId === 'custom' ? null : getLabelTemplateById(labelTemplateId)), [labelTemplateId]);

  const layout = useMemo(() => {
    if (layoutKind === 'sheet-grid') return buildGridLayout({ paper, grid });
    if (layoutKind === 'label-sheet') {
      const template = labelTemplate || {
        columns: 3,
        rows: 7,
        marginTopMm: 15,
        marginLeftMm: 7,
        pitchXmm: 70,
        pitchYmm: 40,
        labelWidthMm: 63.5,
        labelHeightMm: 38.1,
        cornerRadiusMm: 2,
      };
      return buildLabelLayout({ paper, template, startRow, startCol, offsetTopMm, offsetLeftMm });
    }
    return buildGridLayout({ paper, grid });
  }, [grid, labelTemplate, layoutKind, offsetLeftMm, offsetTopMm, paper, startCol, startRow]);

  const buildSlotPages = useCallback(({ items, slotCount, skip }) => {
    const safeItems = Array.isArray(items) ? items : [];
    const totalSlots = Math.max(1, Math.trunc(slotCount || 1));
    const firstSkip = Math.max(0, Math.trunc(skip || 0));

    const pages = [];
    let idx = 0;

    const first = new Array(totalSlots).fill(null);
    for (let s = firstSkip; s < totalSlots && idx < safeItems.length; s += 1) {
      first[s] = safeItems[idx];
      idx += 1;
    }
    pages.push(first);

    while (idx < safeItems.length) {
      const page = new Array(totalSlots).fill(null);
      for (let s = 0; s < totalSlots && idx < safeItems.length; s += 1) {
        page[s] = safeItems[idx];
        idx += 1;
      }
      pages.push(page);
    }

    return pages;
  }, []);

  const visibleSlots = useMemo(() => {
    const slots = [];
    const itemsToPlace = selectedItems;
    const slotCount = layout.slotCount || 0;
    const skip = layout.skip || 0;

    for (let idx = 0; idx < slotCount; idx += 1) {
      const itemIndex = idx - skip;
      if (itemIndex < 0) {
        slots.push(null);
        continue;
      }
      slots.push(itemsToPlace[itemIndex] || null);
    }

    return slots;
  }, [layout.skip, layout.slotCount, selectedItems]);

	  const previewScale = useMemo(() => {
	    const naturalWidthPx = mmToPx(paper.widthMm);
	    const naturalHeightPx = mmToPx(paper.heightMm);
	    if (!naturalWidthPx || !naturalHeightPx) return 1;

	    const fallbackMaxWidthPx = 560;
	    const fallbackScale = Math.min(1, fallbackMaxWidthPx / naturalWidthPx);

	    const availableWidthPx = Math.max(0, Number(previewViewport.width || 0) - 24);
	    const availableHeightPx = Math.max(0, Number(previewViewport.height || 0) - 24);
	    if (!availableWidthPx || !availableHeightPx) return fallbackScale;

	    return Math.min(1, availableWidthPx / naturalWidthPx, availableHeightPx / naturalHeightPx);
	  }, [paper.heightMm, paper.widthMm, previewViewport.height, previewViewport.width]);

  const ensureQr = useCallback(async ({ key, url, widthPx }) => {
    const cacheKey = `${key}:${widthPx}`;
    if (qrCache[cacheKey]) return qrCache[cacheKey];
    if (!url) return '';
    try {
      const dataUrl = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: widthPx });
      setQrCache((prev) => ({ ...prev, [cacheKey]: dataUrl }));
      return dataUrl;
    } catch (err) {
      console.warn('Failed to generate QR', err);
      return '';
    }
  }, [qrCache]);

  const loadCreativeAssetDataUrl = useCallback(async (assetId) => {
    const id = Number(assetId);
    if (!id) return '';
    if (logoCache[String(id)]) return logoCache[String(id)];

    const token = localStorage.getItem('token');
    if (!token) return '';

    try {
      const res = await fetch(`/api/creative/assets/${id}/files/preview`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch asset (${res.status})`);
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      setLogoCache((prev) => ({ ...prev, [String(id)]: dataUrl }));
      return dataUrl;
    } catch (err) {
      console.warn('Failed to load logo asset', err);
      return '';
    }
  }, [logoCache]);

  const loadPlaqueDetails = useCallback(async (plaqueId) => {
    const id = Number(plaqueId);
    if (!id) return null;
    if (plaqueDetailsCache[String(id)]) return plaqueDetailsCache[String(id)];
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const res = await fetch(`/api/plaques/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load plaque (${res.status})`);
      const data = await res.json().catch(() => ({}));
      const plaque = data.plaque || data;
      setPlaqueDetailsCache((prev) => ({ ...prev, [String(id)]: plaque }));
      return plaque;
    } catch (err) {
      console.warn('Failed to load plaque details', err);
      return null;
    }
  }, [plaqueDetailsCache]);

  useEffect(() => {
    let cancelled = false;

    async function primeLogos() {
      const needsLogo = design.showLogo || design.contentStyle === 'plaque';
      if (!needsLogo) return;

      const previewSlots = visibleSlots.slice(0, 18).filter(Boolean);
      for (const slot of previewSlots) {
        if (cancelled) return;

        let desiredLogoAssetId = slot.logoAssetId || null;
        if (slot.source === 'plaques' && slot.plaqueId) {
          const details = await loadPlaqueDetails(slot.plaqueId);
          desiredLogoAssetId = details?.selected_asset_id || details?.selectedAssetId || desiredLogoAssetId;
        }
        if (desiredLogoAssetId) {
          await loadCreativeAssetDataUrl(desiredLogoAssetId);
        }
      }
    }

    primeLogos();

    return () => {
      cancelled = true;
    };
  }, [design.contentStyle, design.showLogo, loadCreativeAssetDataUrl, loadPlaqueDetails, visibleSlots]);

  useEffect(() => {
    let cancelled = false;

    async function primePreview() {
      const previewSlots = visibleSlots.slice(0, 24).filter(Boolean);
      if (!previewSlots.length) return;
      const widthPx = 160;
      for (const slot of previewSlots) {
        if (cancelled) return;
        await ensureQr({ key: slot.key, url: slot.url, widthPx });
      }
    }

    primePreview();

    return () => {
      cancelled = true;
    };
  }, [ensureQr, visibleSlots]);

  const toggleSelected = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateTypography = (key, patch) => {
    setDesign((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] && typeof prev[key] === 'object' ? prev[key] : {}),
        ...patch,
      },
    }));
  };

  const selectAllFiltered = () => {
    setSelectedKeys(new Set(filteredItems.map((row) => row.key)));
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const buildRenderedPages = useCallback(async () => {
    const widthPx = getQrWidthPx({ sizeMm: design.qrSizeMm, dpi: design.renderDpi });
    const slotPages = buildSlotPages({ items: selectedItems, slotCount: layout.slotCount, skip: layout.skip });
    const pages = [];

    for (const pageSlots of slotPages) {
      const renderedSlots = [];
      for (const slot of pageSlots) {
        if (!slot) {
          renderedSlots.push(null);
          continue;
        }

        const qrDataUrl = await ensureQr({ key: slot.key, url: slot.url, widthPx });

        let logoDataUrl = '';
        const needsLogo = (design.showLogo || design.contentStyle === 'plaque');
        if (needsLogo) {
          let desiredLogoAssetId = slot.logoAssetId || null;
          if (slot.source === 'plaques' && slot.plaqueId) {
            const details = await loadPlaqueDetails(slot.plaqueId);
            desiredLogoAssetId = details?.selected_asset_id || details?.selectedAssetId || desiredLogoAssetId;
          }
          logoDataUrl = desiredLogoAssetId ? await loadCreativeAssetDataUrl(desiredLogoAssetId) : '';
        }

        let description = slot.description || '';
        if (slot.source === 'plaques' && slot.plaqueId) {
          const details = await loadPlaqueDetails(slot.plaqueId);
          const englishText = details?.english_text || details?.englishText || '';
          const parsed = parsePlaqueContent(englishText);
          const firstText = parsed?.blocks?.find((b) => b.type === 'text' && b.text)?.text || '';
          description = (firstText || description || '').trim();
        }

	        renderedSlots.push({
	          title: slot.title,
	          subtitle: slot.subtitle,
	          description,
	          priceText: slot.priceText,
	          headerText: slot.headerText || '',
	          tableNumber: slot.tableNumber || null,
	          qrDataUrl,
	          logoDataUrl,
	        });
	      }
      pages.push(renderedSlots);
    }

    return { pages, widthPx };
  }, [
    buildSlotPages,
    design.contentStyle,
    design.qrSizeMm,
    design.renderDpi,
    design.showLogo,
    ensureQr,
    layout.skip,
    layout.slotCount,
    loadCreativeAssetDataUrl,
    loadPlaqueDetails,
    selectedItems,
  ]);

  const handlePrint = async () => {
    if (printingRef.current) return;
    if (!selectedItems.length) {
      alert('Select at least one QR code to print.');
      return;
    }

    printingRef.current = true;
    try {
      const { pages } = await buildRenderedPages();

      const html = buildPrintHtml({
        title: 'Suitestream.tv Print Shop',
        layout,
        pages,
        design,
      });
      await printHtmlInIframe(html);
    } catch (err) {
      console.error('Print failed', err);
      alert(err.message || 'Print failed.');
    } finally {
      printingRef.current = false;
    }
  };

  const handleDownloadPagePng = async () => {
    if (printingRef.current) return;
    if (!selectedItems.length) {
      alert('Select at least one QR code first.');
      return;
    }
    printingRef.current = true;
    try {
      const { pages } = await buildRenderedPages();
      const firstPage = Array.isArray(pages) && pages.length ? pages[0] : [];
      const canvas = await renderPageToCanvas({ layout, slots: firstPage, design, dpi: design.renderDpi });
      const dataUrl = canvas.toDataURL('image/png');
      triggerDownload(dataUrl, 'print-shop-page.png');
    } catch (err) {
      console.error('Download page failed', err);
      alert(err.message || 'Failed to export page.');
    } finally {
      printingRef.current = false;
    }
  };

	  const tableCount = catalogs?.menu?.menu?.tableCount || 0;
	  const hasTableSelection = useMemo(
	    () => selectedItems.some((row) => Boolean(row?.tableNumber)),
	    [selectedItems]
	  );

	  const applyPreset = (preset) => {
	    if (preset === 'price-tags') {
	      setLayoutKind('label-sheet');
      const firstA4 = LABEL_SHEET_TEMPLATES.find((t) => t.paperId === 'A4') || LABEL_SHEET_TEMPLATES[0];
      if (firstA4?.id) setLabelTemplateId(firstA4.id);
      setStartRow(1);
      setStartCol(1);
      setOffsetTopMm(0);
      setOffsetLeftMm(0);
      setPaperId('A4');
      setDesign((prev) => ({
        ...prev,
        contentStyle: 'qrTitle',
        textPosition: 'right',
        showHeader: false,
        headerText: 'Scan to View',
        showBorder: true,
        showTitle: true,
        showSubtitle: false,
        showPrice: true,
        showDescription: false,
        showLogo: false,
        headerTypography: { ...(prev.headerTypography || {}), fontFamily: 'Inter', sizeMm: 3.2 },
        titleTypography: { ...(prev.titleTypography || {}), fontFamily: 'Inter', sizeMm: 3.2 },
        subtitleTypography: { ...(prev.subtitleTypography || {}), fontFamily: 'Inter', sizeMm: 2.8 },
        priceTypography: { ...(prev.priceTypography || {}), fontFamily: 'Inter', sizeMm: 3.0 },
        descriptionTypography: { ...(prev.descriptionTypography || {}), fontFamily: 'Inter', sizeMm: 2.6 },
        qrSizeMm: 22,
        renderDpi: 300,
      }));
	      return;
	    }

	    if (preset === 'sticky-labels') {
	      setLayoutKind('label-sheet');
	      const firstA4 = LABEL_SHEET_TEMPLATES.find((t) => t.paperId === 'A4') || LABEL_SHEET_TEMPLATES[0];
	      if (firstA4?.id) setLabelTemplateId(firstA4.id);
	      setStartRow(1);
	      setStartCol(1);
	      setOffsetTopMm(0);
	      setOffsetLeftMm(0);
	      setPaperId('A4');
	      setDesign((prev) => ({
	        ...prev,
	        contentStyle: 'qrTitle',
	        textPosition: 'right',
	        showHeader: false,
	        showBorder: false,
	        showTitle: true,
	        showSubtitle: false,
	        showPrice: false,
	        showDescription: false,
	        showLogo: false,
	        qrSizeMm: 20,
	        renderDpi: 300,
	      }));
	      return;
	    }

	    if (preset === 'menu-tables') {
	      setLayoutKind('sheet-grid');
	      setPaperId('A4');
	      setGrid((prev) => ({ ...prev, columns: 2, rows: 3, marginMm: 10, gapMm: 8 }));
	      setDesign((prev) => ({
	        ...prev,
	        contentStyle: 'qrTitle',
	        textPosition: 'bottom',
	        showHeader: true,
	        headerText: 'Scan for Menu',
	        tableLabelText: 'Table',
	        showTableLabel: true,
	        showTableNumber: true,
	        tableSubtitleText: '',
	        showBorder: true,
	        showTitle: true,
	        showSubtitle: false,
	        showPrice: false,
	        showDescription: false,
	        showLogo: false,
        headerTypography: { ...(prev.headerTypography || {}), fontFamily: 'Inter', sizeMm: 4.2 },
        titleTypography: { ...(prev.titleTypography || {}), fontFamily: 'Inter', sizeMm: 3.6 },
        subtitleTypography: { ...(prev.subtitleTypography || {}), fontFamily: 'Inter', sizeMm: 3.1 },
        priceTypography: { ...(prev.priceTypography || {}), fontFamily: 'Inter', sizeMm: 3.2 },
        qrSizeMm: 42,
        renderDpi: 300,
      }));
      setSourceFilter((prev) => ({ ...prev, tables: true }));
      setSearch('');
      setSelectedKeys(new Set(allItems.filter((row) => row.source === 'tables').map((row) => row.key)));
      setStep('layout');
      return;
    }

    if (preset === 'plaques') {
      setLayoutKind('sheet-grid');
      setPaperId('A4');
      setGrid((prev) => ({ ...prev, columns: 1, rows: 1, marginMm: 12, gapMm: 0, cornerRadiusMm: 8 }));
      setDesign((prev) => ({
        ...prev,
        contentStyle: 'plaque',
        textPosition: 'bottom',
        showHeader: false,
        showBorder: true,
        showTitle: true,
        showSubtitle: false,
        showPrice: false,
        showDescription: true,
        showLogo: true,
        titleTypography: { ...(prev.titleTypography || {}), fontFamily: 'Inter', sizeMm: 4.2 },
        subtitleTypography: { ...(prev.subtitleTypography || {}), fontFamily: 'Inter', sizeMm: 3.4 },
        priceTypography: { ...(prev.priceTypography || {}), fontFamily: 'Inter', sizeMm: 3.6 },
        descriptionTypography: { ...(prev.descriptionTypography || {}), fontFamily: 'Inter', sizeMm: 3.0 },
        logoSizeMm: 26,
        qrSizeMm: 90,
        renderDpi: 300,
      }));
      setSourceFilter((prev) => ({ ...prev, plaques: true }));
      setSearch('');
      setStep('layout');
      return;
    }
  };

  return (
    <div className="bg-gray-900 rounded p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-semibold">Print Shop</div>
	          <div className="text-sm text-gray-400">
	            Create table menu codes, info plaques, price tags, and sticky labels within standard page sizes.
	          </div>
	        </div>
        <div className="flex flex-wrap items-center gap-2">
	          <div className="flex flex-wrap items-center gap-2">
	            <button
	              className="px-2 py-1 rounded bg-gray-800 text-white text-xs"
	              onClick={() => applyPreset('price-tags')}
	              title="Label sheet preset for price tags (title + price + QR)"
	            >
	              Price Tags
	            </button>
	            <button
	              className="px-2 py-1 rounded bg-gray-800 text-white text-xs"
	              onClick={() => applyPreset('sticky-labels')}
	              title="Label sheet preset for small sticky labels (QR + title)"
	            >
	              Sticky Labels
	            </button>
	            <button
	              className="px-2 py-1 rounded bg-gray-800 text-white text-xs"
	              onClick={() => applyPreset('menu-tables')}
	              disabled={!tableCount}
	              title="Create table QR codes with a “Scan for Menu” header"
	            >
	              Menu Tables
	            </button>
            <button
              className="px-2 py-1 rounded bg-gray-800 text-white text-xs"
              onClick={() => applyPreset('plaques')}
              title="Full-page plaque preset (logo + text + QR)"
            >
              Plaques
            </button>
          </div>
          <div className="flex items-center gap-2">
            <select
              className="px-3 py-1 rounded bg-gray-800 text-white border border-gray-700"
              value={activeTemplateId}
              onChange={(e) => setActiveTemplateId(e.target.value)}
              disabled={!siteId || templatesLoading}
              title={!siteId ? 'Select a site to use templates.' : 'Saved print templates'}
            >
              <option value="">Templates…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <button
              className="px-2 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
              onClick={saveNewTemplate}
              disabled={!siteId}
              title="Save current layout/design as a new template"
            >
              Save
            </button>
            <button
              className="px-2 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
              onClick={updateTemplate}
              disabled={!activeTemplate}
              title="Update selected template with current settings"
            >
              Update
            </button>
            <button
              className="px-2 py-1 rounded bg-red-800 text-white disabled:bg-gray-800 disabled:text-gray-500"
              onClick={deleteTemplate}
              disabled={!activeTemplate}
              title="Delete selected template"
            >
              Delete
            </button>
          </div>
          <button
            className="px-3 py-1 rounded bg-gray-700 text-white"
            onClick={fetchData}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
            onClick={handlePrint}
            disabled={!selectedItems.length}
          >
            Print
          </button>
          <button
            className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
            onClick={handleDownloadPagePng}
            disabled={!selectedItems.length}
          >
            Download Page PNG
          </button>
        </div>
      </div>

      {error && <div className="mb-3 text-sm text-red-300">{error}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          className={`px-3 py-1 rounded ${step === 'choose' ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-200'}`}
          onClick={() => setStep('choose')}
        >
          Choose QR Codes
        </button>
        <button
          className={`px-3 py-1 rounded ${step === 'layout' ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-200'} disabled:bg-gray-900 disabled:text-gray-600`}
          onClick={() => setStep('layout')}
          disabled={!selectedItems.length}
          title={!selectedItems.length ? 'Select at least one QR code first.' : ''}
        >
          Layout &amp; Preview
        </button>
      </div>

      {step === 'choose' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="bg-gray-950 border border-gray-800 rounded p-3">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-sm font-semibold">Choose QR Codes</div>
              <div className="flex items-center gap-2">
                <button className="text-xs px-2 py-1 rounded bg-gray-800" onClick={selectAllFiltered}>
                  Select all
                </button>
                <button className="text-xs px-2 py-1 rounded bg-gray-800" onClick={clearSelection}>
                  Clear
                </button>
              </div>
            </div>

            <input
              className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800 mb-3"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search plaques, items, tables…"
            />

            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { key: 'plaques', label: 'Plaques' },
                { key: 'menu', label: 'Menu' },
                { key: 'tables', label: `Tables${tableCount ? ` (${tableCount})` : ''}` },
                { key: 'products', label: 'Products' },
                { key: 'services', label: 'Services' },
              ].map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1"
                >
                  <input
                    type="checkbox"
                    checked={Boolean(sourceFilter[opt.key])}
                    onChange={() => setSourceFilter((prev) => ({ ...prev, [opt.key]: !prev[opt.key] }))}
                  />
                  <span className="text-gray-200">{opt.label}</span>
                </label>
              ))}
            </div>

            <div className="text-xs text-gray-400 mb-2">
              Showing {filteredItems.length} · Selected {selectedItems.length}
            </div>

            <div className="max-h-[640px] overflow-auto border border-gray-800 rounded">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-gray-950 z-10">
                  <tr className="text-left text-gray-400">
                    <th className="py-2 px-2 w-10"></th>
                    <th className="py-2 px-2">QR</th>
                    <th className="py-2 px-2">Item</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={3} className="py-3 px-2 text-gray-400">Loading…</td>
                    </tr>
                  )}
                  {!loading && !filteredItems.length && (
                    <tr>
                      <td colSpan={3} className="py-3 px-2 text-gray-400">Nothing setup yet.</td>
                    </tr>
                  )}
                  {!loading && filteredItems.map((row) => {
                    const isSelected = selectedKeys.has(row.key);
                    const qrPreviewKey = `${row.key}:160`;
                    const qrPreview = qrCache[qrPreviewKey] || '';
                    return (
                      <tr key={row.key} className="border-t border-gray-800 hover:bg-gray-900">
                        <td className="py-2 px-2 align-top">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelected(row.key)}
                          />
                        </td>
                        <td className="py-2 px-2 align-top">
                          {qrPreview ? (
                            <img
                              src={qrPreview}
                              alt=""
                              className="bg-white p-1 rounded"
                              style={{ width: 44, height: 44 }}
                            />
                          ) : (
                            <div className="w-11 h-11 rounded bg-gray-800 border border-gray-700" />
                          )}
                        </td>
                        <td className="py-2 px-2 align-top">
                          <div className="text-xs text-gray-500">{row.sourceLabel}{row.sectionName ? ` · ${row.sectionName}` : ''}</div>
                          <div className="font-semibold text-gray-100">{row.title}</div>
                          {!!row.priceText && <div className="text-xs text-gray-300">{row.priceText}</div>}
                          {!!row.subtitle && <div className="text-xs text-gray-400">{row.subtitle}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="text-xs text-gray-400">
                Tip: use Start row/column on the next tab to reuse partially-used label sheets.
              </div>
              <button
                className="px-3 py-1 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                onClick={() => setStep('layout')}
                disabled={!selectedItems.length}
              >
                Next: Layout &amp; Preview
              </button>
            </div>
          </div>

          <div className="bg-gray-950 border border-gray-800 rounded p-3">
            <div className="text-sm font-semibold mb-2">Selection Summary</div>
            <div className="text-sm text-gray-300 mb-3">
              {selectedItems.length ? `${selectedItems.length} selected` : 'Nothing selected yet.'}
            </div>
            {!!selectedItems.length && (
              <div className="max-h-[640px] overflow-auto border border-gray-800 rounded">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-950 z-10">
                    <tr className="text-left text-gray-400">
                      <th className="py-2 px-2">Item</th>
                      <th className="py-2 px-2 w-24">Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((row) => (
                      <tr key={`sel-${row.key}`} className="border-t border-gray-800">
                        <td className="py-2 px-2">
                          <div className="text-xs text-gray-500">{row.sourceLabel}</div>
                          <div className="font-semibold text-gray-100">{row.title}</div>
                        </td>
                        <td className="py-2 px-2">
                          <button className="px-2 py-1 rounded bg-gray-800 text-white text-xs" onClick={() => toggleSelected(row.key)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

	      {step === 'layout' && (
	        <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-4">
          <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-semibold">Layout</div>
                <button className="text-xs px-2 py-1 rounded bg-gray-800" onClick={() => setStep('choose')}>
                  Back to selection
                </button>
              </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="text-sm text-gray-200">
                Layout type
                <select
                  className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                  value={layoutKind}
                  onChange={(e) => setLayoutKind(e.target.value)}
                >
                  <option value="label-sheet">Label sheet (sticky)</option>
                  <option value="sheet-grid">Paper grid (cards)</option>
                </select>
              </label>

              <label className="text-sm text-gray-200">
                Paper size
                <select
                  className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                  value={paperId}
                  onChange={(e) => setPaperId(e.target.value)}
                  disabled={layoutKind === 'label-sheet' && labelTemplateId !== 'custom'}
                >
                  {PAPER_SIZES.map((p) => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
              </label>
            </div>

            {layoutKind === 'label-sheet' && (
              <div className="mt-3 space-y-3">
                <label className="text-sm text-gray-200">
                  Label sheet template
                  <select
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={labelTemplateId}
                    onChange={(e) => {
                      setLabelTemplateId(e.target.value);
                      setStartRow(1);
                      setStartCol(1);
                    }}
                  >
                    {LABEL_SHEET_TEMPLATES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                    <option value="custom">Custom…</option>
                  </select>
                </label>

                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm text-gray-200">
                    Start row
                    <input
                      type="number"
                      min={1}
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={startRow}
                      onChange={(e) => setStartRow(e.target.value)}
                    />
                  </label>
                  <label className="text-sm text-gray-200">
                    Start column
                    <input
                      type="number"
                      min={1}
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={startCol}
                      onChange={(e) => setStartCol(e.target.value)}
                    />
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="text-sm text-gray-200">
                    Nudge down (mm)
                    <input
                      type="number"
                      step="0.1"
                      min={-20}
                      max={20}
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={offsetTopMm}
                      onChange={(e) => setOffsetTopMm(e.target.value)}
                    />
                  </label>
                  <label className="text-sm text-gray-200">
                    Nudge right (mm)
                    <input
                      type="number"
                      step="0.1"
                      min={-20}
                      max={20}
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={offsetLeftMm}
                      onChange={(e) => setOffsetLeftMm(e.target.value)}
                    />
                  </label>
                </div>

                {labelTemplateId === 'custom' && (
                  <div className="text-xs text-gray-400">
                    Custom label geometry editing is coming next. For now, pick a template and use Start row/column for partial sheets.
                  </div>
                )}
              </div>
            )}

            {layoutKind === 'sheet-grid' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-200">
                  Columns
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={grid.columns}
                    onChange={(e) => setGrid((prev) => ({ ...prev, columns: Number(e.target.value) }))}
                  />
                </label>
                <label className="text-sm text-gray-200">
                  Rows
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={grid.rows}
                    onChange={(e) => setGrid((prev) => ({ ...prev, rows: Number(e.target.value) }))}
                  />
                </label>
                <label className="text-sm text-gray-200">
                  Margin (mm)
                  <input
                    type="number"
                    min={0}
                    max={40}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={grid.marginMm}
                    onChange={(e) => setGrid((prev) => ({ ...prev, marginMm: Number(e.target.value) }))}
                  />
                </label>
                <label className="text-sm text-gray-200">
                  Gap (mm)
                  <input
                    type="number"
                    min={0}
                    max={30}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={grid.gapMm}
                    onChange={(e) => setGrid((prev) => ({ ...prev, gapMm: Number(e.target.value) }))}
                  />
                </label>
              </div>
            )}
          </div>

            <div className="bg-gray-950 border border-gray-800 rounded p-3">
              <div className="text-sm font-semibold mb-2">Design</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-200">
                  Style
                  <select
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.contentStyle}
                    onChange={(e) => setDesign((prev) => ({ ...prev, contentStyle: e.target.value }))}
                  >
                    <option value="qrTitle">Border + header + QR</option>
                    <option value="qrOnly">QR only</option>
                    <option value="plaque">Plaque (logo + text + QR)</option>
                  </select>
                </label>

                <label className="text-sm text-gray-200">
                  QR size (mm)
                  <input
                    type="number"
                    min={10}
                    max={200}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.qrSizeMm}
                    onChange={(e) => setDesign((prev) => ({ ...prev, qrSizeMm: Number(e.target.value) }))}
                  />
                </label>
              </div>

              {design.contentStyle === 'qrTitle' && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="text-sm text-gray-200">
                    Text position
                    <select
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={safePosition(design.textPosition, 'bottom')}
                      onChange={(e) => setDesign((prev) => ({ ...prev, textPosition: e.target.value }))}
                    >
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                  </label>
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
	                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
	                  <input
	                    type="checkbox"
	                    checked={Boolean(design.showTitle)}
	                    onChange={() => setDesign((prev) => ({ ...prev, showTitle: !prev.showTitle }))}
	                  />
	                  <span className="text-gray-200">{hasTableSelection ? 'Table Number' : 'Title'}</span>
	                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showSubtitle)}
                    onChange={() => setDesign((prev) => ({ ...prev, showSubtitle: !prev.showSubtitle }))}
                  />
                  <span className="text-gray-200">Subtitle</span>
                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showDescription)}
                    onChange={() => setDesign((prev) => ({ ...prev, showDescription: !prev.showDescription }))}
                    disabled={design.contentStyle !== 'plaque'}
                  />
                  <span className={`${design.contentStyle !== 'plaque' ? 'text-gray-500' : 'text-gray-200'}`}>Description</span>
                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showBorder)}
                    onChange={() => setDesign((prev) => ({ ...prev, showBorder: !prev.showBorder }))}
                  />
                  <span className="text-gray-200">Border</span>
                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showHeader)}
                    onChange={() => setDesign((prev) => ({ ...prev, showHeader: !prev.showHeader }))}
                    disabled={design.contentStyle !== 'qrTitle'}
                  />
                  <span className={`${design.contentStyle !== 'qrTitle' ? 'text-gray-500' : 'text-gray-200'}`}>Header</span>
                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showPrice)}
                    onChange={() => setDesign((prev) => ({ ...prev, showPrice: !prev.showPrice }))}
                  />
                  <span className="text-gray-200">Price</span>
                </label>
                <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-1">
                  <input
                    type="checkbox"
                    checked={Boolean(design.showLogo)}
                    onChange={() => setDesign((prev) => ({ ...prev, showLogo: !prev.showLogo }))}
                    disabled={design.contentStyle !== 'plaque'}
                  />
                  <span className={`${design.contentStyle !== 'plaque' ? 'text-gray-500' : 'text-gray-200'}`}>Logo</span>
                </label>
              </div>

              {design.contentStyle === 'qrTitle' && design.showHeader && (
                <div className="mt-3 space-y-3">
                  <label className="block text-sm text-gray-200">
                    Header text
                    <input
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={design.headerText}
                      onChange={(e) => setDesign((prev) => ({ ...prev, headerText: e.target.value }))}
                      placeholder="e.g. Scan for Menu"
                    />
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="text-sm text-gray-200">
                      Header font
                      <div className="mt-1">
                        <FontSelect
                          value={safeFontFamily(design.headerTypography?.fontFamily)}
                          options={getFontOptions(design.headerTypography?.fontFamily)}
                          onChange={(value) => updateTypography('headerTypography', { fontFamily: value })}
                        />
                      </div>
                    </div>
                    <label className="text-sm text-gray-200">
                      Header size (mm)
                      <input
                        type="number"
                        min={1.2}
                        max={30}
                        step={0.1}
                        className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                        value={design.headerTypography?.sizeMm ?? 3.4}
                        onChange={(e) => updateTypography('headerTypography', { sizeMm: Number(e.target.value) })}
                      />
                    </label>
                  </div>
	                </div>
	              )}

	              {design.contentStyle === 'qrTitle' && hasTableSelection && design.showTitle && (
	                <div className="mt-3 space-y-3">
	                  <div className="text-xs text-gray-400">
	                    Table number formatting (only applies to Menu Tables).
	                  </div>
	                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
	                    <label className="text-sm text-gray-200">
	                      Table label text
	                      <input
	                        className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
	                        value={design.tableLabelText ?? 'Table'}
	                        onChange={(e) => setDesign((prev) => ({ ...prev, tableLabelText: e.target.value }))}
	                        placeholder="e.g. Table, Booth, Desk"
	                      />
	                    </label>
	                    <div className="text-sm text-gray-200">
	                      Display
	                      <div className="mt-1 flex flex-wrap gap-2">
	                        <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-2">
	                          <input
	                            type="checkbox"
	                            checked={design.showTableLabel !== false}
	                            onChange={() => setDesign((prev) => ({ ...prev, showTableLabel: !(prev.showTableLabel !== false) }))}
	                          />
	                          <span className="text-gray-200">Label</span>
	                        </label>
	                        <label className="flex items-center gap-2 text-xs bg-gray-900 border border-gray-800 rounded px-2 py-2">
	                          <input
	                            type="checkbox"
	                            checked={design.showTableNumber !== false}
	                            onChange={() => setDesign((prev) => ({ ...prev, showTableNumber: !(prev.showTableNumber !== false) }))}
	                          />
	                          <span className="text-gray-200">Number</span>
	                        </label>
	                      </div>
	                    </div>
	                  </div>
	                </div>
	              )}

	              {design.contentStyle === 'qrTitle' && hasTableSelection && design.showSubtitle && (
	                <div className="mt-3">
	                  <label className="block text-sm text-gray-200">
	                    Subtitle text (tables)
	                    <input
	                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
	                      value={design.tableSubtitleText ?? ''}
	                      onChange={(e) => setDesign((prev) => ({ ...prev, tableSubtitleText: e.target.value }))}
	                      placeholder="Optional second line (e.g. Order & Pay)"
	                    />
	                  </label>
	                </div>
	              )}

	              <div className="mt-4 pt-3 border-t border-gray-800">
	                <div className="text-xs text-gray-400 uppercase tracking-wider">Typography</div>
	              </div>
	              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
	                <div className="text-sm text-gray-200">
	                  {hasTableSelection ? 'Table Number font' : 'Title font'}
	                  <div className="mt-1">
	                    <FontSelect
	                      value={safeFontFamily(design.titleTypography?.fontFamily)}
	                      options={getFontOptions(design.titleTypography?.fontFamily)}
	                      onChange={(value) => updateTypography('titleTypography', { fontFamily: value })}
	                    />
	                  </div>
	                </div>
	                <label className="text-sm text-gray-200">
	                  {hasTableSelection ? 'Table Number size (mm)' : 'Title size (mm)'}
	                  <input
	                    type="number"
	                    min={1.2}
	                    max={30}
                    step={0.1}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.titleTypography?.sizeMm ?? 3.6}
                    onChange={(e) => updateTypography('titleTypography', { sizeMm: Number(e.target.value) })}
                  />
                </label>

                <div className="text-sm text-gray-200">
                  Subtitle font
                  <div className="mt-1">
                    <FontSelect
                      value={safeFontFamily(design.subtitleTypography?.fontFamily)}
                      options={getFontOptions(design.subtitleTypography?.fontFamily)}
                      onChange={(value) => updateTypography('subtitleTypography', { fontFamily: value })}
                    />
                  </div>
                </div>
                <label className="text-sm text-gray-200">
                  Subtitle size (mm)
                  <input
                    type="number"
                    min={1.2}
                    max={30}
                    step={0.1}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.subtitleTypography?.sizeMm ?? 3.1}
                    onChange={(e) => updateTypography('subtitleTypography', { sizeMm: Number(e.target.value) })}
                  />
                </label>

                <div className="text-sm text-gray-200">
                  Price font
                  <div className="mt-1">
                    <FontSelect
                      value={safeFontFamily(design.priceTypography?.fontFamily)}
                      options={getFontOptions(design.priceTypography?.fontFamily)}
                      onChange={(value) => updateTypography('priceTypography', { fontFamily: value })}
                    />
                  </div>
                </div>
                <label className="text-sm text-gray-200">
                  Price size (mm)
                  <input
                    type="number"
                    min={1.2}
                    max={30}
                    step={0.1}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.priceTypography?.sizeMm ?? 3.2}
                    onChange={(e) => updateTypography('priceTypography', { sizeMm: Number(e.target.value) })}
                  />
                </label>
              </div>

              {design.contentStyle === 'plaque' && design.showLogo && (
                <label className="block text-sm text-gray-200 mt-3">
                  Logo size (mm)
                  <input
                    type="number"
                    min={6}
                    max={60}
                    className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                    value={design.logoSizeMm}
                    onChange={(e) => setDesign((prev) => ({ ...prev, logoSizeMm: Number(e.target.value) }))}
                  />
                </label>
              )}

              {design.contentStyle === 'plaque' && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="text-sm text-gray-200">
                    Description font
                    <div className="mt-1">
                      <FontSelect
                        value={safeFontFamily(design.descriptionTypography?.fontFamily)}
                        options={getFontOptions(design.descriptionTypography?.fontFamily)}
                        onChange={(value) => updateTypography('descriptionTypography', { fontFamily: value })}
                      />
                    </div>
                  </div>
                  <label className="text-sm text-gray-200">
                    Description size (mm)
                    <input
                      type="number"
                      min={1.2}
                      max={30}
                      step={0.1}
                      className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                      value={design.descriptionTypography?.sizeMm ?? 3.0}
                      onChange={(e) => updateTypography('descriptionTypography', { sizeMm: Number(e.target.value) })}
                    />
                  </label>
                </div>
              )}

	              <div className="mt-4 pt-3 border-t border-gray-800">
	                <div className="text-xs text-gray-400 uppercase tracking-wider">Export</div>
	              </div>
	              <label className="block text-sm text-gray-200 mt-3">
	                Render DPI (export quality)
	                <select
	                  className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
	                  value={design.renderDpi}
	                  onChange={(e) => setDesign((prev) => ({ ...prev, renderDpi: Number(e.target.value) }))}
                >
                  <option value={150}>150</option>
                  <option value={300}>300</option>
                  <option value={600}>600</option>
                </select>
              </label>
            </div>
          </div>

          <div className="space-y-3">
            <div className="bg-gray-950 border border-gray-800 rounded p-3">
	              <div className="text-sm font-semibold mb-2">Preview</div>
	              <div className="text-xs text-gray-400 mb-3">
	                Preview auto-fits to this panel; final alignment depends on printer scaling. Use 100% / Actual size when printing.
	              </div>

	              <div
	                ref={previewViewportRef}
	                className="border border-gray-800 rounded bg-gray-900 p-3 overflow-hidden flex items-center justify-center"
	                style={{ height: 'min(72vh, 760px)' }}
	              >
                <div
                  className="bg-white"
                  style={{
                    width: mmToPx(paper.widthMm) * previewScale,
                    height: mmToPx(paper.heightMm) * previewScale,
                    transformOrigin: 'top left',
                  }}
                >
                  <div
                    style={{
                      width: mmToPx(paper.widthMm) * previewScale,
                      height: mmToPx(paper.heightMm) * previewScale,
                      paddingTop: mmToPx(layout.marginTopMm) * previewScale,
                      paddingLeft: mmToPx(layout.marginLeftMm) * previewScale,
                      boxSizing: 'border-box',
                      fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${layout.columns}, ${mmToPx(layout.labelWidthMm) * previewScale}px)`,
                        gridAutoRows: `${mmToPx(layout.labelHeightMm) * previewScale}px`,
                        columnGap: `${mmToPx(layout.gapXmm) * previewScale}px`,
                        rowGap: `${mmToPx(layout.gapYmm) * previewScale}px`,
                      }}
                    >
		                      {visibleSlots.map((slot, idx) => {
		                        const isEmpty = !slot;
		                        const qrPreviewKey = slot ? `${slot.key}:160` : '';
		                        const qrPreview = slot ? qrCache[qrPreviewKey] : '';
		                        const header = slot?.headerText || design.headerText || 'Scan';
		                        const textPosition = safePosition(design.textPosition, 'bottom');
		                        const showHeader = design.contentStyle === 'qrTitle' && design.showHeader;
		                        const showLogo = design.contentStyle === 'plaque' && design.showLogo;

	                        const cellWmm = Number(layout.labelWidthMm || 40);
	                        const cellHmm = Number(layout.labelHeightMm || 20);
	                        const paddingMm = 2.2;
	                        const paddingPx = mmToPx(paddingMm) * previewScale;

	                        const headerTyp = computeEffectiveTypographyMm({
	                          cellWmm,
	                          cellHmm,
	                          typography: design.headerTypography,
	                          kind: 'header',
	                        });
	                        const titleTyp = computeEffectiveTypographyMm({
	                          cellWmm,
	                          cellHmm,
	                          typography: design.titleTypography,
	                          kind: 'title',
	                        });
	                        const subtitleTyp = computeEffectiveTypographyMm({
	                          cellWmm,
	                          cellHmm,
	                          typography: design.subtitleTypography,
	                          kind: 'subtitle',
	                        });
	                        const priceTyp = computeEffectiveTypographyMm({
	                          cellWmm,
	                          cellHmm,
	                          typography: design.priceTypography,
	                          kind: 'price',
	                        });
	                        const descTyp = computeEffectiveTypographyMm({
	                          cellWmm,
	                          cellHmm,
	                          typography: design.descriptionTypography,
	                          kind: 'description',
	                        });

	                        const reservedTextMm = (() => {
	                          if (design.contentStyle !== 'qrTitle') return 0;
	                          if (textPosition === 'left' || textPosition === 'right') return 0;
	                          let mm = paddingMm * 2;
	                          if (showHeader) mm += headerTyp.sizeMm * 1.6 + 1.2;
	                          if (design.showTitle) mm += titleTyp.sizeMm * 1.35 + 0.8;
	                          if (design.showSubtitle) mm += subtitleTyp.sizeMm * 1.25 + 0.6;
	                          if (design.showPrice) mm += priceTyp.sizeMm * 1.25 + 0.6;
	                          return mm;
	                        })();

	                        const effectiveQrMm = computeQrSizeMm({
	                          cellWmm,
	                          cellHmm,
	                          paddingMm,
	                          desiredQrMm: design.qrSizeMm,
	                          textPosition,
	                          reservedTextMm,
	                        });

	                        const qrPx = mmToPx(effectiveQrMm) * previewScale;
	                        const logoMm = Math.min(Number(design.logoSizeMm || 18), cellHmm * 0.28);
	                        const logoPx = mmToPx(logoMm) * previewScale;

	                        const resolveLogoDataUrl = () => {
	                          if (!slot || !showLogo) return '';
	                          let desiredLogoAssetId = slot.logoAssetId || null;
	                          if (slot.source === 'plaques' && slot.plaqueId) {
                            const details = plaqueDetailsCache[String(slot.plaqueId)] || null;
                            desiredLogoAssetId = details?.selected_asset_id || details?.selectedAssetId || desiredLogoAssetId;
                          }
                          const cached = desiredLogoAssetId ? logoCache[String(desiredLogoAssetId)] : '';
                          if (cached) return cached;
                          return '';
                        };

                        const logoDataUrl = resolveLogoDataUrl();

	                        const borderStyle = design.showBorder ? '1px solid #111' : '1px solid rgba(0,0,0,0.08)';
	                        const radiusPx = mmToPx(layout.cornerRadiusMm) * previewScale;

		                        const metaTitle = resolveSlotTitleText(slot, design);
		                        const metaSubtitle = resolveSlotSubtitleText(slot, design);
		                        const metaPrice = slot?.priceText || '';
		                        const metaDesc = slot?.description || '';

	                        const headerFontPx = mmToPx(headerTyp.sizeMm) * previewScale;
	                        const titleFontPx = mmToPx(titleTyp.sizeMm) * previewScale;
	                        const subtitleFontPx = mmToPx(subtitleTyp.sizeMm) * previewScale;
	                        const priceFontPx = mmToPx(priceTyp.sizeMm) * previewScale;
	                        const descFontPx = mmToPx(descTyp.sizeMm) * previewScale;

	                        const qrTitleFlexDirection = textPosition === 'left'
	                          ? 'row'
	                          : textPosition === 'right'
	                            ? 'row'
	                            : 'column';
	                        const qrTitleOrder = {
	                          top: ['text', 'qr'],
	                          bottom: ['qr', 'text'],
	                          left: ['text', 'qr'],
	                          right: ['qr', 'text'],
	                        }[textPosition] || ['qr', 'text'];

	                        return (
	                          <div
	                            key={`slot-${idx}`}
	                            style={{
	                              border: borderStyle,
	                              borderRadius: radiusPx,
	                              overflow: 'hidden',
	                              background: isEmpty ? 'transparent' : '#fff',
	                              color: '#111',
	                              display: 'flex',
	                              flexDirection: design.contentStyle === 'qrTitle' ? qrTitleFlexDirection : 'column',
	                            }}
	                          >
	                            {!slot && <div style={{ flex: 1 }} />}

	                            {slot && design.contentStyle === 'qrOnly' && (
	                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: paddingPx }}>
	                                {qrPreview && <img src={qrPreview} alt="" style={{ width: qrPx, height: qrPx, background: '#fff' }} />}
	                              </div>
	                            )}

	                            {slot && design.contentStyle === 'qrTitle' && (
	                              <>
	                                {qrTitleOrder[0] === 'text' && (
	                                  <div
	                                    style={{
	                                      flex: (textPosition === 'left' || textPosition === 'right') ? 1 : 'unset',
	                                      minWidth: 0,
	                                      display: 'flex',
	                                      flexDirection: 'column',
	                                      justifyContent: 'center',
	                                      padding: paddingPx,
	                                      textAlign: 'center',
	                                      overflow: 'hidden',
	                                    }}
	                                  >
	                                    {showHeader && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(headerTyp.fontFamily),
	                                          fontSize: headerFontPx,
	                                          fontWeight: 800,
	                                          letterSpacing: '0.06em',
	                                          textTransform: 'uppercase',
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                          borderBottom: (textPosition === 'top') ? '1px solid #111' : 'none',
	                                          paddingBottom: 2 * previewScale,
	                                          marginBottom: 2 * previewScale,
	                                        }}
	                                      >
	                                        {header || metaTitle}
	                                      </div>
	                                    )}

		                                    {design.showTitle && !!metaTitle && (!showHeader || !header || header !== metaTitle) && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(titleTyp.fontFamily),
	                                          fontSize: titleFontPx,
	                                          fontWeight: 800,
	                                          lineHeight: 1.1,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaTitle}
	                                      </div>
	                                    )}
	                                    {design.showSubtitle && !!metaSubtitle && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(subtitleTyp.fontFamily),
	                                          fontSize: subtitleFontPx,
	                                          fontWeight: 600,
	                                          opacity: 0.92,
	                                          marginTop: 2 * previewScale,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaSubtitle}
	                                      </div>
	                                    )}
	                                    {design.showPrice && !!metaPrice && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(priceTyp.fontFamily),
	                                          fontSize: priceFontPx,
	                                          fontWeight: 800,
	                                          marginTop: 2 * previewScale,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaPrice}
	                                      </div>
	                                    )}
	                                  </div>
	                                )}

	                                <div
	                                  style={{
	                                    flex: (textPosition === 'left' || textPosition === 'right') ? '0 0 auto' : 1,
	                                    display: 'flex',
	                                    alignItems: 'center',
	                                    justifyContent: 'center',
	                                    padding: paddingPx,
	                                    boxSizing: 'border-box',
	                                  }}
	                                >
	                                  {qrPreview && <img src={qrPreview} alt="" style={{ width: qrPx, height: qrPx, background: '#fff' }} />}
	                                </div>

	                                {qrTitleOrder[1] === 'text' && (
	                                  <div
	                                    style={{
	                                      flex: (textPosition === 'left' || textPosition === 'right') ? 1 : 'unset',
	                                      minWidth: 0,
	                                      display: 'flex',
	                                      flexDirection: 'column',
	                                      justifyContent: 'center',
	                                      padding: paddingPx,
	                                      textAlign: 'center',
	                                      overflow: 'hidden',
	                                    }}
	                                  >
	                                    {showHeader && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(headerTyp.fontFamily),
	                                          fontSize: headerFontPx,
	                                          fontWeight: 800,
	                                          letterSpacing: '0.06em',
	                                          textTransform: 'uppercase',
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                          borderBottom: (textPosition === 'bottom') ? '1px solid #111' : 'none',
	                                          paddingBottom: 2 * previewScale,
	                                          marginBottom: 2 * previewScale,
	                                        }}
	                                      >
	                                        {header || metaTitle}
	                                      </div>
	                                    )}

		                                    {design.showTitle && !!metaTitle && (!showHeader || !header || header !== metaTitle) && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(titleTyp.fontFamily),
	                                          fontSize: titleFontPx,
	                                          fontWeight: 800,
	                                          lineHeight: 1.1,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaTitle}
	                                      </div>
	                                    )}
	                                    {design.showSubtitle && !!metaSubtitle && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(subtitleTyp.fontFamily),
	                                          fontSize: subtitleFontPx,
	                                          fontWeight: 600,
	                                          opacity: 0.92,
	                                          marginTop: 2 * previewScale,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaSubtitle}
	                                      </div>
	                                    )}
	                                    {design.showPrice && !!metaPrice && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(priceTyp.fontFamily),
	                                          fontSize: priceFontPx,
	                                          fontWeight: 800,
	                                          marginTop: 2 * previewScale,
	                                          whiteSpace: 'nowrap',
	                                          overflow: 'hidden',
	                                          textOverflow: 'ellipsis',
	                                        }}
	                                      >
	                                        {metaPrice}
	                                      </div>
	                                    )}
	                                  </div>
	                                )}
	                              </>
	                            )}

	                            {slot && design.contentStyle === 'plaque' && (
	                              <div style={{ flex: 1, padding: paddingPx, display: 'flex', flexDirection: 'column', gap: 6 * previewScale }}>
	                                <div style={{ display: 'flex', gap: 8 * previewScale, alignItems: 'flex-start' }}>
	                                  {showLogo && (
	                                    logoDataUrl
	                                      ? <img src={logoDataUrl} alt="" style={{ width: logoPx, height: logoPx, objectFit: 'contain', borderRadius: 6 * previewScale }} />
	                                      : <div style={{ width: logoPx, height: logoPx, background: '#eee', borderRadius: 6 * previewScale }} />
	                                  )}
	                                  <div style={{ flex: 1, minWidth: 0 }}>
		                                    {design.showTitle && !!metaTitle && (
	                                      <div style={{ fontFamily: safeFontFamily(titleTyp.fontFamily), fontSize: titleFontPx, fontWeight: 800, lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
	                                        {metaTitle}
	                                      </div>
	                                    )}
	                                    {design.showSubtitle && !!metaSubtitle && (
	                                      <div style={{ fontFamily: safeFontFamily(subtitleTyp.fontFamily), fontSize: subtitleFontPx, fontWeight: 600, marginTop: 2 * previewScale, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
	                                        {metaSubtitle}
	                                      </div>
	                                    )}
	                                    {design.showPrice && !!metaPrice && (
	                                      <div style={{ fontFamily: safeFontFamily(priceTyp.fontFamily), fontSize: priceFontPx, fontWeight: 800, marginTop: 2 * previewScale, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
	                                        {metaPrice}
	                                      </div>
	                                    )}
	                                    {design.showDescription && !!metaDesc && (
	                                      <div
	                                        style={{
	                                          fontFamily: safeFontFamily(descTyp.fontFamily),
	                                          fontSize: descFontPx,
	                                          lineHeight: 1.2,
	                                          marginTop: 4 * previewScale,
	                                          overflow: 'hidden',
	                                          display: '-webkit-box',
                                          WebkitLineClamp: 4,
                                          WebkitBoxOrient: 'vertical',
                                        }}
                                      >
                                        {metaDesc}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {qrPreview && <img src={qrPreview} alt="" style={{ width: qrPx, height: qrPx, background: '#fff' }} />}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
